import { memo, Children, isValidElement, cloneElement, Fragment, useMemo } from 'react';

import { highlighter } from './Highlight.utils';
import { ErrorBoundary } from '../ErrorBoundary';
import { cn } from '../../lib/styles';

/**
 * Shared props for highlight functionality.
 */
interface SharedHighlightProps {
  /**
   * The text to highlight. Can be a single string or an array of strings.
   * All occurrences of the specified text(s) will be highlighted.
   */
  highlight: string | string[];
  /**
   * Optional custom class name to apply to highlighted text spans.
   * @default 'bg-yellow-300'
   */
  highlightClassName?: string;
  /**
   * When true, only matches complete words (word boundary matching).
   * When false, matches partial text within words.
   * @default false
   */
  matchWholeWords?: boolean;
}

interface NonRecursiveHighlightProps extends SharedHighlightProps {
  children: string;
}

interface RecursiveHighlightProps extends SharedHighlightProps {
  children: React.ReactNode;
}

/**
 * Props for the Highlight component.
 * Supports either a string child (non-recursive) or React nodes (recursive).
 */
export type HighlightComponentProps = NonRecursiveHighlightProps | RecursiveHighlightProps;

const HighlightedText = ({
  text,
  highlight,
  highlightClassName,
  matchWholeWords,
}: SharedHighlightProps & { text: string }) => {
  const highlightChunks = useMemo(
    () => highlighter(text, highlight, matchWholeWords),
    [text, highlight, matchWholeWords],
  );

  return (
    <>
      {highlightChunks.map(({ chunk, highlighted }, i) =>
        highlighted ? (
          <span key={i} data-highlight={chunk} className={cn('bg-blue-2', highlightClassName)}>
            {chunk}
          </span>
        ) : (
          <Fragment key={i}>{chunk}</Fragment>
        ),
      )}
    </>
  );
};

const NonRecursiveHighlight = ({
  children,
  highlight,
  highlightClassName,
  matchWholeWords,
}: Omit<NonRecursiveHighlightProps, 'recursive'>) => {
  return (
    <HighlightedText
      text={children}
      highlight={highlight}
      highlightClassName={highlightClassName}
      matchWholeWords={matchWholeWords}
    />
  );
};

const RecursiveHighlight = ({
  children,
  highlight,
  highlightClassName,
  matchWholeWords,
}: Omit<RecursiveHighlightProps, 'recursive'>) => {
  const renderRecursively = (childNodes: React.ReactNode): React.ReactNode => {
    return Children.map(childNodes, (child) => {
      if (typeof child === 'string') {
        return (
          <HighlightedText
            text={child}
            highlight={highlight}
            highlightClassName={highlightClassName}
            matchWholeWords={matchWholeWords}
          />
        );
      }

      if (isValidElement(child)) {
        const childProps = child.props as { children?: React.ReactNode };
        return cloneElement(child, {
          ...childProps,
          children: renderRecursively(childProps.children),
        } as typeof childProps);
      }

      return (
        <HighlightedText
          text={String(child)}
          highlight={highlight}
          highlightClassName={highlightClassName}
          matchWholeWords={matchWholeWords}
        />
      );
    });
  };

  return <>{renderRecursively(children)}</>;
};

const HighlightBase = (props: HighlightComponentProps) => {
  if (!props.highlight || (Array.isArray(props.highlight) && props.highlight.length === 0)) {
    return props.children;
  }

  if (typeof props.children === 'string') {
    return (
      <NonRecursiveHighlight
        highlight={props.highlight}
        highlightClassName={props.highlightClassName}
        matchWholeWords={props.matchWholeWords}
      >
        {props.children}
      </NonRecursiveHighlight>
    );
  }

  return (
    <ErrorBoundary fallback={props.children}>
      <RecursiveHighlight
        highlight={props.highlight}
        highlightClassName={props.highlightClassName}
        matchWholeWords={props.matchWholeWords}
      >
        {props.children}
      </RecursiveHighlight>
    </ErrorBoundary>
  );
};

/**
 * A flexible text highlighting component that wraps matching text in styled spans.
 *
 * The component supports two modes of operation:
 * - **Non-recursive mode**: When children is a plain string, it directly highlights matches.
 * - **Recursive mode**: When children contains React elements, it recursively traverses
 *   the component tree to find and highlight text nodes.
 *
 * @example
 * // Basic string highlighting
 * <Highlight highlight="world">Hello, world!</Highlight>
 *
 * @example
 * // Multiple highlights
 * <Highlight highlight={['Hello', 'world']}>Hello, world!</Highlight>
 *
 * @example
 * // Whole word matching
 * <Highlight highlight="ability" matchWholeWords>
 *   The ability to move without instability
 * </Highlight>
 *
 * @example
 * // Nested React elements (recursive mode)
 * <Highlight highlight="important">
 *   <div>
 *     <span>This is important text</span>
 *   </div>
 * </Highlight>
 *
 * @example
 * // Custom highlight styling
 * <Highlight highlight="custom" highlightClassName="bg-blue-200 font-bold">
 *   Custom styled highlight
 * </Highlight>
 */
export const Highlight = memo(HighlightBase);
